import { useCallback, useEffect, useState } from 'react'
import { Copy, Link2, Trash2 } from 'lucide-react'
import ApiService from '../../services/api'
import { useNotification } from '../../context/NotificationContext'

export const SELECAO_LABELS = {
  pendente: 'Aguardando triagem',
  aprovado: 'Aprovado para técnica',
  nao_selecionado: 'Não selecionado',
}

const DECISAO_SUCESSO = {
  aprovado: 'Candidato liberado para a etapa técnica.',
  nao_selecionado: 'Candidato mantido apenas na triagem.',
  pendente: 'Decisão revertida para pendente.',
}

// Áreas que continuam no banco de questões, mas não estão sendo aplicadas agora.
const AREAS_NAO_APLICAVEIS = new Set(['civel', 'trabalhista', 'tributario'])

const STATUS_LABELS = {
  liberada: 'Aguardando candidato',
  enviada: 'Prova concluída',
  revogada: 'Link excluído',
}

function formatDateTime(value) {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('pt-BR')
}

// Link direto da prova: abre o formulário público com o código do convite.
export function provaUrl(token) {
  return `${window.location.origin}/formulario?prova=${encodeURIComponent(token)}`
}

export function SelecaoTecnicaChip({ value }) {
  const status = value || 'pendente'
  return <span className={`selecao-chip ${status}`}>{SELECAO_LABELS[status] || SELECAO_LABELS.pendente}</span>
}

// Liga/desliga geral: ao enviar o questionário, o candidato é aprovado e segue direto para a prova do seu nível.
export function ProvaAutomaticaToggle() {
  const { showError, showSuccess } = useNotification()
  const [ativa, setAtiva] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    ApiService.getConfiguracaoFormularioPublico().then((result) => {
      if (result.success) setAtiva(Boolean(result.data?.provaTecnicaAutomatica))
    })
  }, [])

  const alternar = async () => {
    const nova = !ativa
    const pergunta = nova
      ? 'Ligar a prova técnica automática? A partir de agora, quem enviar o questionário com nível Júnior, Pleno ou Sênior será aprovado para a etapa técnica e seguirá direto para a prova.'
      : 'Desligar a prova técnica automática? Os próximos candidatos voltarão a aguardar a triagem do RH.'
    if (!window.confirm(pergunta)) return
    setSaving(true)
    const result = await ApiService.updateConfiguracaoFormularioPublico({ provaTecnicaAutomatica: nova })
    setSaving(false)
    if (!result.success) return showError(result.error || 'Erro ao salvar a configuração')
    setAtiva(Boolean(result.data?.provaTecnicaAutomatica))
    showSuccess(nova ? 'Prova técnica automática ligada.' : 'Prova técnica automática desligada.')
  }

  const explicacao = ativa
    ? 'Ligada: ao enviar o questionário, o candidato é aprovado para a etapa técnica e segue direto para a prova do seu nível (Júnior → Advogado Júnior; Pleno ou Sênior → Advogado Pleno/Sênior). Quem informa "Nenhum" termina no questionário.'
    : 'Desligada: os candidatos terminam no questionário e aguardam a triagem do RH, que gera a prova pela ficha.'

  // Campo compacto, no mesmo formato dos filtros da tela de Fichas de Candidatos.
  return (
    <div className="users-select-group submissions-filter-field prova-automatica-field">
      <span className="users-field-label" id="prova-automatica-label">Prova técnica logo após o questionário</span>
      <button
        type="button"
        role="switch"
        aria-checked={Boolean(ativa)}
        aria-labelledby="prova-automatica-label"
        className="prova-automatica-switch"
        disabled={saving || ativa === null}
        onClick={alternar}
        title={ativa === null ? 'Carregando…' : explicacao}
      >
        <span aria-hidden="true" />
        {ativa === null ? '…' : ativa ? 'Ligada' : 'Desligada'}
      </button>
    </div>
  )
}

function RespostasProva({ prova }) {
  const letter = (index) => String.fromCharCode(65 + index)
  return (
    <details className="prova-respostas">
      <summary>Ver respostas da prova</summary>
      <h4>Questões objetivas</h4>
      {(prova.exam?.questions || []).map((q, i) => {
        const chosen = prova.answers?.[q.id]
        const answered = chosen !== undefined && chosen !== null
        const status = !answered ? 'blank' : chosen === q.correct ? 'right' : 'wrong'
        return (
          <div key={q.id} className={`prova-questao ${status}`}>
            <div className="prova-questao-head">
              <span>QUESTÃO {i + 1}{q.topic ? ` · ${q.topic}` : ''}</span>
              <em>{status === 'right' ? 'Acertou' : status === 'wrong' ? 'Errou' : 'Sem resposta'}</em>
            </div>
            <b>{q.text}</b>
            <ol className="prova-opcoes">
              {(q.options || []).map((option, j) => (
                <li key={j} className={`${j === q.correct ? 'correta' : ''} ${j === chosen ? 'escolhida' : ''}`.trim()}>
                  <span className="prova-letra">{letter(j)}</span>
                  <span>{option}</span>
                  {j === chosen && <small>Resposta do candidato</small>}
                  {j === q.correct && <small>Gabarito</small>}
                </li>
              ))}
            </ol>
            {q.rationale && <p className="prova-justificativa"><b>Justificativa do gabarito: </b>{q.rationale}</p>}
          </div>
        )
      })}
      <h4>Questões discursivas</h4>
      {(prova.exam?.essays || []).map((q, i) => (
        <div key={q.id} className="prova-questao">
          <div className="prova-questao-head"><span>DISCURSIVA {i + 1}{q.topic ? ` · ${q.topic}` : ''}</span></div>
          <b>{q.text}</b>
          <p className="prova-discursiva">{prova.essays?.[q.id] || 'Sem resposta registrada'}</p>
        </div>
      ))}
    </details>
  )
}

function ProvaRegistro({ prova, onRevogar }) {
  const { showSuccess } = useNotification()
  const concluida = prova.status === 'enviada' || (prova.status === 'revogada' && prova.statusAnterior === 'enviada')
  const revogada = prova.status === 'revogada'
  const pct = concluida && prova.score != null && prova.totalObjetivas > 0 ? Math.round((100 * prova.score) / prova.totalObjetivas) : null

  const copiar = async () => {
    const url = provaUrl(prova.token)
    try {
      await navigator.clipboard.writeText(url)
      showSuccess('Link da prova copiado. Envie ao candidato de forma privada.')
    } catch {
      window.prompt('Copie o link da prova:', url)
    }
  }

  return (
    <article className="prova-registro">
      <div className="prova-registro-top">
        <div>
          <strong>{prova.areaNome || prova.area}</strong>
          <small>
            {prova.origem === 'automatica' ? 'Gerada automaticamente após o questionário' : 'Gerada pelo RH'} em {formatDateTime(prova.createdAt)}
            {prova.enviadaEm ? ` · enviada em ${formatDateTime(prova.enviadaEm)}` : ''}
          </small>
        </div>
        <span className={`prova-status ${revogada ? 'revogada' : concluida ? 'enviada' : 'liberada'}`}>{STATUS_LABELS[prova.status]}</span>
      </div>
      {concluida && (
        <p className="prova-resultado">
          Questões objetivas: <strong>{prova.score ?? '-'} de {prova.totalObjetivas} acertos{pct != null ? ` (${pct}%)` : ''}</strong> · Discursivas para revisão humana
        </p>
      )}
      <div className="prova-acoes">
        {!revogada && !concluida && (
          <button type="button" className="btn btn-outline btn-sm" onClick={copiar}><Copy size={14} /> Copiar link</button>
        )}
        {!revogada && (
          <button type="button" className="btn btn-outline btn-sm prova-excluir" onClick={() => onRevogar(prova)}><Trash2 size={14} /> Excluir link</button>
        )}
        {revogada && <small>Acesso invalidado em {formatDateTime(prova.revogadaEm)}</small>}
      </div>
      {concluida && <RespostasProva prova={prova} />}
    </article>
  )
}

export default function ProvasTecnicasFicha({ ficha, onFichaChange }) {
  const { showError, showSuccess } = useNotification()
  const [areas, setAreas] = useState([])
  const [area, setArea] = useState('')
  const [provas, setProvas] = useState([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [showRevoked, setShowRevoked] = useState(false)
  const selecao = ficha.selecaoTecnica || 'pendente'

  const loadProvas = useCallback(async () => {
    const result = await ApiService.getProvasTecnicasFicha(ficha.id)
    setLoading(false)
    if (!result.success) return showError(result.error || 'Erro ao carregar as provas técnicas')
    setProvas(Array.isArray(result.data) ? result.data : [])
  }, [ficha.id, showError])

  useEffect(() => {
    ApiService.getAreasProvaTecnica().then((result) => {
      if (!result.success) return
      const list = Array.isArray(result.data) ? result.data : []
      setAreas(list)
      setArea((current) => current || list[0]?.key || '')
    })
  }, [])

  useEffect(() => { loadProvas() }, [loadProvas])

  const decidir = async (decisao) => {
    if (selecao === 'aprovado' && decisao !== 'aprovado'
      && !window.confirm('Retirar aprovação? Os links técnicos ainda pendentes serão invalidados. Resultados já concluídos serão preservados.')) return
    setBusy(true)
    const result = await ApiService.updateSelecaoTecnica(ficha.id, decisao)
    setBusy(false)
    if (!result.success) return showError(result.error || 'Erro ao salvar decisão')
    onFichaChange(result.data)
    showSuccess(DECISAO_SUCESSO[decisao])
    loadProvas()
  }

  const gerar = async () => {
    if (!area) return
    setBusy(true)
    const result = await ApiService.gerarProvaTecnica(ficha.id, area)
    setBusy(false)
    if (!result.success) return showError(result.error || 'Erro ao gerar a prova')
    showSuccess('Link de prova gerado. Use "Copiar link" para enviar ao candidato.')
    loadProvas()
  }

  const revogar = async (prova) => {
    const question = prova.status === 'enviada'
      ? 'Excluir o link de acesso? As respostas e a nota desta prova serão preservadas no histórico.'
      : 'Excluir este link? O candidato não poderá mais acessar a prova por ele.'
    if (!window.confirm(question)) return
    const result = await ApiService.revogarProvaTecnica(prova.id)
    if (!result.success) return showError(result.error || 'Erro ao excluir o link')
    showSuccess('Link excluído.')
    loadProvas()
  }

  // Links excluídos ficam ocultos por padrão; a prova enviada continua no histórico se o RH quiser vê-la.
  const revogadas = provas.filter((prova) => prova.status === 'revogada').length
  const visiveis = showRevoked ? provas : provas.filter((prova) => prova.status !== 'revogada')

  return (
    <>
      <div className="selecao-decisao">
        <h3>Decisão do RH: avanço para a prova técnica</h3>
        <p>A decisão é manual e não depende automaticamente das notas. Só é possível gerar prova para candidatos selecionados.</p>
        <SelecaoTecnicaChip value={selecao} />
        <div className="selecao-decisao-acoes">
          <button type="button" className="selecao-aprovar" disabled={busy || selecao === 'aprovado'} onClick={() => decidir('aprovado')}>Selecionar para prova técnica</button>
          <button type="button" className="selecao-recusar" disabled={busy || selecao === 'nao_selecionado'} onClick={() => decidir('nao_selecionado')}>Não selecionar</button>
          <button type="button" disabled={busy || selecao === 'pendente'} onClick={() => decidir('pendente')}>Voltar para análise</button>
        </div>
        <p className="selecao-decisao-foot">A ficha e as notas comportamentais permanecem no sistema independentemente da decisão.</p>
      </div>

      <article className="users-detail-card provas-tecnicas-card">
        <h3>Provas técnicas</h3>
        {selecao === 'aprovado' ? (
          <div className="prova-gerar">
            <label>
              Nova prova
              <select className="users-select" value={area} onChange={(event) => setArea(event.target.value)}>
                {areas.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.name} · {item.objetivas} objetivas, {item.discursivas} discursivas{AREAS_NAO_APLICAVEIS.has(item.key) ? ' (não aplicável no momento)' : ''}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="btn btn-primary btn-sm" disabled={busy || !area} onClick={gerar}><Link2 size={14} /> Gerar link de prova</button>
          </div>
        ) : (
          <p className="prova-aviso">Selecione o candidato para a prova técnica para poder gerar um link.</p>
        )}
        {revogadas > 0 && (
          <label className="prova-toggle">
            <input type="checkbox" checked={showRevoked} onChange={(event) => setShowRevoked(event.target.checked)} />
            Mostrar links excluídos ({revogadas})
          </label>
        )}
        {loading ? <p className="prova-aviso">Carregando provas…</p> : null}
        {!loading && visiveis.length === 0 ? (
          <p className="prova-aviso">{provas.length ? 'Nenhuma prova ativa ou concluída. Os links excluídos estão ocultos.' : 'Nenhuma prova gerada para este candidato.'}</p>
        ) : null}
        <div className="prova-lista">
          {visiveis.map((prova) => <ProvaRegistro key={prova.id} prova={prova} onRevogar={revogar} />)}
        </div>
      </article>
    </>
  )
}
